<script setup lang="ts">
  import { computed, onUnmounted, ref } from 'vue';

  interface HoverListTooltipItem {
    id: number | string;
    text: string;
  }

  const props = defineProps<{
    label: string;
    title: string;
    items: HoverListTooltipItem[];
    testId: string;
  }>();

  const VIEWPORT_MARGIN = 8;
  // Empirically measured: a title block (~46px) plus ~28px per row, capped once
  // the list starts scrolling internally (`max-h-72` on the <ul>, ~334px total).
  const BASE_HEIGHT = 46;
  const ITEM_HEIGHT = 28;
  const MAX_TOOLTIP_HEIGHT = 334;

  const showTooltip = ref(false);
  const hideTimer = ref<ReturnType<typeof setTimeout> | null>(null);
  const anchorTop = ref(0);
  const anchorBottom = ref(0);
  const anchorLeft = ref(0);
  const flipped = ref(false);

  const tooltipStyle = computed(() => ({
    top: flipped.value
      ? `${anchorBottom.value + VIEWPORT_MARGIN}px`
      : `${Math.max(VIEWPORT_MARGIN, anchorTop.value - VIEWPORT_MARGIN)}px`,
    left: `${anchorLeft.value}px`,
  }));

  const cancelHide = () => {
    if (hideTimer.value) {
      clearTimeout(hideTimer.value);
      hideTimer.value = null;
    }
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimer.value = setTimeout(() => {
      showTooltip.value = false;
    }, 200);
  };

  onUnmounted(cancelHide);

  const onTriggerEnter = (event: MouseEvent) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const estimatedHeight = Math.min(
      BASE_HEIGHT + ITEM_HEIGHT * props.items.length,
      MAX_TOOLTIP_HEIGHT
    );

    anchorTop.value = rect.top;
    anchorBottom.value = rect.bottom;
    anchorLeft.value = rect.left + rect.width / 2;
    flipped.value = anchorTop.value - VIEWPORT_MARGIN - estimatedHeight < VIEWPORT_MARGIN;
    cancelHide();
    showTooltip.value = true;
  };

  const onTriggerLeave = () => scheduleHide();
  const onTooltipEnter = () => cancelHide();
  const onTooltipLeave = () => scheduleHide();
</script>

<template>
  <div class="inline-block">
    <span
      class="cursor-pointer text-sm text-gray-07 transition-colors duration-200 hover:text-badge-blue"
      @mouseenter="onTriggerEnter"
      @mouseleave="onTriggerLeave"
    >
      {{ label }}
    </span>

    <div
      v-if="showTooltip"
      :data-testid="testId"
      class="fixed z-[9999] w-64 -translate-x-1/2 rounded-lg border border-border-color bg-white-01 p-3 shadow-lg"
      :class="flipped ? 'translate-y-0' : '-translate-y-full'"
      :style="tooltipStyle"
      @mouseenter="onTooltipEnter"
      @mouseleave="onTooltipLeave"
    >
      <p class="mb-2 text-xs font-semibold text-gray-11">{{ title }}</p>
      <ul class="max-h-72 space-y-1 overflow-y-auto pr-1">
        <li
          v-for="item in items"
          :key="item.id"
          class="rounded px-2 py-1 text-xs bg-gray-09 text-gray-03"
        >
          {{ item.text }}
        </li>
      </ul>
      <div
        class="absolute left-1/2 size-3 -translate-x-1/2 rotate-45 bg-white-01"
        :class="
          flipped
            ? 'top-0 -translate-y-1/2 border-t border-l border-border-color'
            : 'bottom-0 translate-y-1/2 border-b border-r border-border-color'
        "
      />
    </div>
  </div>
</template>
